/**
 * Dashboard Page
 * Overview of contracts, payments, and key metrics
 * Supports filtering by investor country and configurable base currency
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Collapse,
  IconButton,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  Description,
  Payment as PaymentIcon,
  Warning,
  ErrorOutline,
  EventAvailable,
  AssignmentLate,
  LinkOff,
  Groups,
  ExpandMore,
  ExpandLess,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { apiGet } from '../utils/apiClient';
import { getChartPalette, getCapacityColors, getInvestorColors } from '../utils/chartColors';
import { getStatusColor } from '../utils/statusColors';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatCard from '../components/common/StatCard';
import ContractReferenceCell from '../components/contracts/ContractReferenceCell';
import { format } from 'date-fns';
import { formatNumber } from '../utils/validation';
import { exportUpcomingEvents, exportMaturingContracts } from '../utils/excelExport';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../contexts/PermissionContext';
import { MODULES } from '../contexts/PermissionConstants';
import logger from '../utils/logger';

const CURRENCY_SYMBOLS = {
  USD: '$', ZAR: 'R', GBP: '£', EUR: '€',
  BWP: 'P', NAD: 'N$', LSL: 'M',
};

const compactMoney = (value, symbol) => {
  const v = Number(value) || 0;
  if (Math.abs(v) >= 1_000_000) return `${symbol} ${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${symbol} ${(v / 1_000).toFixed(1)}K`;
  return `${symbol} ${formatNumber(v)}`;
};

const RADIAN = Math.PI / 180;
// Word-wrap a label into at most `maxLines` lines, each no wider than `maxChars`.
// The last allowed line is truncated with an ellipsis if text remains.
const wrapLabel = (text, maxChars = 18, maxLines = 2) => {
  const words = String(text).split(' ');
  const lines = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
      if (lines.length === maxLines - 1) break;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  // Anything not yet emitted gets ellipsised onto the final line.
  const consumed = lines.join(' ').split(' ').length;
  if (consumed < words.length && lines.length) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = last.length > maxChars - 1 ? `${last.slice(0, maxChars - 1)}…` : `${last}…`;
  }
  return lines;
};

// Custom pie label renderer: wraps long category names onto up to 2 lines and
// puts the percent/count suffix on its own line. `displayMode` is 'percent' | 'number'.
const renderPieLabel = (displayMode) => ({ cx, cy, midAngle, outerRadius, name, percent, value }) => {
  const radius = outerRadius + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const anchor = x > cx ? 'start' : 'end';
  const nameLines = wrapLabel(name);
  const suffix = displayMode === 'percent' ? `${(percent * 100).toFixed(0)}%` : `(${value})`;
  const lines = [...nameLines, suffix];
  // Vertically centre the multi-line block on the anchor point.
  const lineHeight = 14;
  const startDy = -((lines.length - 1) * lineHeight) / 2;
  return (
    <text x={x} y={y} fill="#555" textAnchor={anchor} dominantBaseline="central" fontSize={12}>
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? startDy : lineHeight}>{line}</tspan>
      ))}
    </text>
  );
};

function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { tenant, loading: tenantLoading } = useTenant();
  const { canRead } = usePermissions();

  // Chart colors derived from the active tenant's brand palette (falls back to
  // default MUI primary/secondary when a tenant has no branding configured).
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const chartPalette = React.useMemo(() => getChartPalette(primary, secondary), [primary, secondary]);
  const capacityColors = React.useMemo(() => getCapacityColors(primary, secondary), [primary, secondary]);
  const investorColors = React.useMemo(() => getInvestorColors(primary), [primary]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [statusDisplayMode, setStatusDisplayMode] = useState('percent');
  const [productDisplayMode, setProductDisplayMode] = useState('percent');
  const [capacityDisplayMode, setCapacityDisplayMode] = useState('percent');
  const [approvalsTotal, setApprovalsTotal] = useState(0);
  const [approvalsSummary, setApprovalsSummary] = useState(null);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [maturingExpanded, setMaturingExpanded] = useState(false);
  const [capacityExpanded, setCapacityExpanded] = useState(false);
  // Day-window filter for the Maturing Soon / Payments expandable tables
  // (T3-047). 'all' = full 90 days; default to the nearest window.
  const [maturingWindow, setMaturingWindow] = useState('0-30');
  const [eventsWindow, setEventsWindow] = useState('0-30');

  // Filter state
  const [selectedCountries, setSelectedCountries] = useState([]);
  // Opening base currency comes from the tenant's own Default/Base Currency setting
  // (Tenant Settings -> Currency); 'USD' is only the last-resort fallback for a tenant
  // with the setting unset. Seeded synchronously below, before the first fetch, so the
  // async tenant value does not trigger a second /reports/dashboard call.
  const [baseCurrency, setBaseCurrency] = useState(tenant?.defaultCurrency || 'USD');
  const baseCurrencySeeded = useRef(Boolean(tenant?.defaultCurrency));
  if (!baseCurrencySeeded.current && tenant?.defaultCurrency) {
    // Tenant resolved after mount: adopt its currency during render, ahead of the
    // gated initial load, so the first request already carries the right currency.
    baseCurrencySeeded.current = true;
    setBaseCurrency(tenant.defaultCurrency);
  }
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState(['USD', 'ZAR']);

  const refDataLoaded = useRef(false);

  const currencySymbol = CURRENCY_SYMBOLS[baseCurrency] || baseCurrency;

  const makeDisplayChangeHandler = (setter) => (event, newMode) => {
    if (newMode !== null) setter(newMode);
  };

  // Day-window filter helpers (T3-047): filter a window_bucket-tagged list, and
  // map the selected window to a human label for headings / exports.
  const inWindow = (rows, w) => (w === 'all' ? rows : (rows || []).filter(r => r.windowBucket === w));
  const windowLabel = (w) => ({
    all: 'Next 90 Days',
    '0-30': '0–30 Days',
    '31-60': '31–60 Days',
    '61-90': '61–90 Days',
  }[w] || 'Next 90 Days');

  const loadFilterOptions = useCallback(async () => {
    if (refDataLoaded.current) return;
    try {
      const countriesData = await apiGet('/refdata/countries?active_only=true');
      const countries = Array.isArray(countriesData) ? countriesData
        : (countriesData?.data || countriesData?.rows || []);
      setAvailableCountries(countries);

      const currencies = [...new Set(countries.map(c => c.currency_code).filter(Boolean))].sort();
      if (currencies.length > 0) {
        setAvailableCurrencies(currencies);
      }
      refDataLoaded.current = true;
    } catch (err) {
      logger.error('Error loading filter options', { message: err.message, status: err.response?.status });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    const storedTenantId = localStorage.getItem('selectedTenantId');

    if (!tenantLoading && (tenantParam || storedTenantId || tenant?.id)) {
      loadFilterOptions();
      if (!cancelled) loadDashboardData();
      if (!cancelled && canRead(MODULES.CONTRACT_MANAGEMENT)) {
        loadApprovalsSummary();
      }
    } else if (!tenantLoading && !tenantParam && !storedTenantId && !tenant?.id) {
      setLoading(false);
      setDashboardData(getEmptyDashboardData());
    }
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantLoading, tenant?.id]);

  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    const storedTenantId = localStorage.getItem('selectedTenantId');
    if (!tenantLoading && (tenantParam || storedTenantId || tenant?.id)) {
      loadDashboardData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountries, baseCurrency]);

  const loadApprovalsSummary = async () => {
    try {
      const response = await apiGet('/approvals/summary');
      if (response.success) {
        const s = response.data || {};
        const sum = Number(s.contract_approvals || 0)
                  + Number(s.verifications || 0)
                  + Number(s.queried_verifications || 0);
        setApprovalsTotal(sum);
        setApprovalsSummary(s);
      }
    } catch (err) {
      logger.error('Error loading approvals summary', { message: err.message, status: err.response?.status });
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCountries.length > 0) {
        params.set('country_ids', selectedCountries.map(c => c.id).join(','));
      }
      params.set('base_currency', baseCurrency);

      const queryString = params.toString();
      const apiData = await apiGet(`/reports/dashboard?${queryString}`);

      const transformedData = transformApiData(apiData);
      setDashboardData(transformedData);
      setError(null);
    } catch (err) {
      logger.error('Error loading dashboard', { message: err.message, status: err.response?.status });
      if (err.response?.status === 429) {
        // Rate limited — a global throttle toast (RateLimitToastBridge) already explains this.
        // Show an honest inline note instead of the generic "failed to load", so the empty
        // dashboard doesn't read as data loss.
        setError('Dashboard temporarily rate limited — please wait a moment and refresh.');
      } else {
        setError('Failed to load dashboard data');
      }
      setDashboardData(getEmptyDashboardData());
    } finally {
      setLoading(false);
    }
  };

  const getEmptyDashboardData = () => ({
    totalPortfolioValue: 0,
    totalPortfolioDraft: 0,
    totalPortfolioApproved: 0,
    capitalByCurrency: [],
    activeContracts: 0,
    pendingApprovals: 0,
    upcomingPayments: [],
    upcomingBuckets: { c30: 0, c60: 0, c90: 0, t30: 0, t60: 0, t90: 0 },
    upcomingPaymentsCount: 0,
    upcomingPaymentsTotal: 0,
    maturingList: [],
    contractsByStatus: [],
    contractsByProduct: [],
    contractsByInvestor: [],
    capitalByIssuer: [],
    programCapacity: [],
    unmatched: { count: 0, total_amount: 0, overdue_count: 0, upcoming_count: 0, overdue_total: 0, upcoming_total: 0 },
    overdue: { count: 0, total_amount: 0, by_currency: [] },
    needsAction: { count: 0, by_currency: [] },
    maturing: { d30: 0, d60: 0, d90: 0, cap30: 0, cap60: 0, cap90: 0 },
  });

  const transformApiData = (apiData) => {
    let activeContracts = 0;
    const contractsByStatus = [];

    if (apiData.contractStats && Array.isArray(apiData.contractStats)) {
      apiData.contractStats.forEach(stat => {
        const count = parseInt(stat.count) || 0;

        contractsByStatus.push({
          name: stat.status || 'Unknown',
          value: count,
        });

        if (stat.status === 'Active') {
          activeContracts = count;
        }
      });
    }

    if (apiData.activeContractsCount) {
      activeContracts = parseInt(apiData.activeContractsCount) || activeContracts;
    }

    // upcomingPayments now spans 90 days with per-window buckets (c30/c60/c90 +
    // t30/t60/t90). count/total_amount are the back-compat 90-day sums (T3-047),
    // so the headline reflects the full 90 days; the table filters by window.
    const up = apiData.upcomingPayments || {};
    const upcomingPaymentsCount = up.count
      ? parseInt(up.count)
      : (parseInt(up.c30) || 0) + (parseInt(up.c60) || 0) + (parseInt(up.c90) || 0);
    const upcomingPaymentsTotal = parseFloat(up.total_amount)
      || ((parseFloat(up.t30) || 0) + (parseFloat(up.t60) || 0) + (parseFloat(up.t90) || 0));
    const upcomingBuckets = {
      c30: parseInt(up.c30) || 0, c60: parseInt(up.c60) || 0, c90: parseInt(up.c90) || 0,
      t30: parseFloat(up.t30) || 0, t60: parseFloat(up.t60) || 0, t90: parseFloat(up.t90) || 0,
    };

    const upcomingPayments = (up.data || []).map(p => {
      const rawAmount = p.amount;
      const hasAmount = rawAmount !== null && rawAmount !== undefined && rawAmount !== '';
      return {
        id: p.id,
        contractId: p.contract_id,
        reference: p.contract_reference || p.reference,
        userReference: p.user_reference,
        investor: p.investor_name || p.investor,
        paymentDate: p.payment_date || p.paymentDate,
        amount: hasAmount ? parseFloat(rawAmount) : null,
        currency: p.currency_code || p.currency || 'ZAR',
        type: p.payment_type || p.type || 'Interest',
        windowBucket: p.window_bucket || '0-30',
      };
    });

    const contractsByProduct = (apiData.contractsByProduct || []).map(p => ({
      name: p.product_code || p.product_name || p.name,
      value: parseInt(p.count) || parseInt(p.value) || 0,
      totalCapital: parseFloat(p.total_capital) || 0,
    }));

    // Each investor carries the full total (drives ordering, the top-9 cut and
    // the "Other" aggregation) plus the Draft / Approved-Active split that backs
    // the stacked bar (draft + approved == value).
    const investorRows = (apiData.capitalByInvestor || []).map(i => ({
      name: i.investor_name || i.name,
      value: parseFloat(i.total_capital) || parseFloat(i.value) || 0,
      draft: parseFloat(i.draft_capital) || 0,
      approved: parseFloat(i.approved_capital) || 0,
    }));
    const contractsByInvestor = investorRows.length > 10
      ? [
          ...investorRows.slice(0, 9),
          {
            name: 'Other',
            value: investorRows.slice(9).reduce((sum, r) => sum + r.value, 0),
            draft: investorRows.slice(9).reduce((sum, r) => sum + r.draft, 0),
            approved: investorRows.slice(9).reduce((sum, r) => sum + r.approved, 0),
          },
        ]
      : investorRows;

    const capitalByIssuer = (apiData.capitalByIssuer || []).map(i => ({
      name: i.issuer_name,
      value: parseFloat(i.total_capital) || 0,
      contractCount: parseInt(i.contract_count) || 0,
    }));

    // programCapacity (T096): each entry already arrives sorted by utilisation
    // DESC NULLS LAST (closest-to-breach first). limit_native NULL = "no limit".
    const programCapacity = (apiData.programCapacity || []).map(p => {
      const limitBase = p.limit_base != null ? parseFloat(p.limit_base) : null;
      const usedBase = parseFloat(p.used_base) || 0;
      const utilisation = p.utilisation != null ? parseFloat(p.utilisation) : null;
      // Stacked-bar segments (in base ccy): used + headroom = limit.
      // The DB hard-blocks over-limit contracts via fn_program_check_limit,
      // so usedBase will never exceed limitBase. NULL-limit programs show
      // used only (no headroom segment) — labelled "No limit" in the tooltip.
      const usedSegment = usedBase;
      const headroomSegment = limitBase != null ? Math.max(limitBase - usedBase, 0) : 0;
      // Percent-of-limit segments for the %-mode view (used% + headroom% = 100).
      // utilisation is already used/limit from the backend; NULL-limit programs
      // have no meaningful percentage → show fully used (headroom 0); the tooltip
      // still reads "No limit" via the labelFormatter.
      const usedPct = utilisation != null ? Math.min(utilisation * 100, 100) : (usedBase > 0 ? 100 : 0);
      const headroomPct = utilisation != null ? Math.max(100 - usedPct, 0) : 0;
      return {
        programId: p.program_id,
        programName: p.program_name,
        issuerId: p.issuer_id,
        issuerName: p.issuer_name,
        currency: p.currency_code,
        limitNative: p.limit_native != null ? parseFloat(p.limit_native) : null,
        usedNative: parseFloat(p.used_native) || 0,
        limitBase,
        usedBase,
        utilisation,
        usedSegment,
        headroomSegment,
        usedPct,
        headroomPct,
      };
    });

    const capitalByCurrency = (apiData.capitalByCurrency || []).map(c => ({
      currency: c.currency_code,
      symbol: c.currency_symbol,
      amount: parseFloat(c.total_amount) || 0,
      baseEquivalent: parseFloat(c.base_equivalent ?? c.zar_equivalent) || 0,
      contractCount: parseInt(c.contract_count) || 0,
      fxRate: parseFloat(c.fx_rate) || null,
      // Draft (Draft + Pending Approval) vs Approved/Active (Approved + Active)
      // split — per-bucket LCY, count and base-equivalent (reconcile to the
      // grand amount / contractCount / baseEquivalent above).
      draftAmount: parseFloat(c.draft_amount) || 0,
      approvedAmount: parseFloat(c.approved_amount) || 0,
      draftCount: parseInt(c.draft_count) || 0,
      approvedCount: parseInt(c.approved_count) || 0,
      draftBaseEquivalent: parseFloat(c.draft_base_equivalent) || 0,
      approvedBaseEquivalent: parseFloat(c.approved_base_equivalent) || 0,
    }));

    const totalPortfolioValue = parseFloat(apiData.totalPortfolioValue ?? apiData.totalPortfolioZAR) || 0;
    // Per-bucket portfolio totals (base ccy) for the Draft / Approved-Active
    // footer rows; draft + approved == totalPortfolioValue.
    const totalPortfolioDraft = parseFloat(apiData.totalPortfolioDraft) || 0;
    const totalPortfolioApproved = parseFloat(apiData.totalPortfolioApproved) || 0;

    const unmatched = apiData.unmatchedPayments || {};
    const overdue = apiData.overduePayments || {};
    const needsAction = apiData.contractsNeedingAction || {};
    const maturing = apiData.maturingSoon || {};

    // Contracts maturing in the next 90 days — backs the "Maturing Soon"
    // click-to-expand table. Each row carries windowBucket (0-30/31-60/61-90,
    // T3-047) so the table can filter by window. Field names mirror the
    // upcoming-events list so the table/export markup is reusable.
    const maturingList = (maturing.list || []).map(c => ({
      contractId: c.contract_id,
      reference: c.contract_reference || c.reference,
      userReference: c.user_reference,
      investor: c.investor_name || c.investor,
      maturityDate: c.maturity_date || c.maturityDate,
      amount: c.amount != null ? parseFloat(c.amount) : null,
      currency: c.currency_code || c.currency || 'ZAR',
      status: c.status,
      windowBucket: c.window_bucket || '0-30',
    }));

    return {
      totalPortfolioValue,
      totalPortfolioDraft,
      totalPortfolioApproved,
      capitalByCurrency,
      activeContracts,
      upcomingPayments,
      upcomingBuckets,
      maturingList,
      upcomingPaymentsCount,
      upcomingPaymentsTotal,
      contractsByStatus,
      contractsByProduct,
      contractsByInvestor,
      capitalByIssuer,
      programCapacity,
      unmatched: {
        count: parseInt(unmatched.count) || 0,
        total_amount: parseFloat(unmatched.total_amount) || 0,
        overdue_count: parseInt(unmatched.overdue_count) || 0,
        upcoming_count: parseInt(unmatched.upcoming_count) || 0,
        overdue_total: parseFloat(unmatched.overdue_total) || 0,
        upcoming_total: parseFloat(unmatched.upcoming_total) || 0,
      },
      overdue: {
        count: parseInt(overdue.count) || 0,
        total_amount: parseFloat(overdue.total_amount) || 0,
        by_currency: Array.isArray(overdue.by_currency) ? overdue.by_currency : [],
      },
      needsAction: {
        count: parseInt(needsAction.count) || 0,
        by_currency: Array.isArray(needsAction.by_currency) ? needsAction.by_currency : [],
      },
      maturing: {
        d30: parseInt(maturing.d30) || 0,
        d60: parseInt(maturing.d60) || 0,
        d90: parseInt(maturing.d90) || 0,
        cap30: parseFloat(maturing.cap30) || 0,
        cap60: parseFloat(maturing.cap60) || 0,
        cap90: parseFloat(maturing.cap90) || 0,
      },
    };
  };

  const handleEventClick = (event) => {
    if (event.type === 'Re-pricing') {
      navigate(`/contracts/${event.contractId}`);
      return;
    }
    // Payment event → Matching, auto-open the match modal for this schedule
    // row. event.id is the contract_amortization_schedule id, which the
    // Matching feeds key on, so the page can find the row and open the dialog.
    navigate(`/payments?scheduleId=${event.id}`);
  };

  if (loading || tenantLoading) {
    return <LoadingSpinner fullPage />;
  }

  const data = dashboardData || getEmptyDashboardData();

  // Top product breakdown (top 3) for the Active Contracts stat card.
  // 3-col layout: count · product name · base-currency capital.
  const topProductBreakdown = data.contractsByProduct.slice(0, 3).map(p => ({
    count: p.value,
    label: p.name,
    value: compactMoney(p.totalCapital, currencySymbol),
  }));

  // Maturing Soon: 3-col layout per disjoint window (count · window · base-ccy
  // capital). Buckets are non-overlapping (T093) so each contract lands in one.
  const maturingBreakdown = [
    { count: data.maturing.d30, label: '0–30 days',  value: compactMoney(data.maturing.cap30, currencySymbol) },
    { count: data.maturing.d60, label: '31–60 days', value: compactMoney(data.maturing.cap60, currencySymbol) },
    { count: data.maturing.d90, label: '61–90 days', value: compactMoney(data.maturing.cap90, currencySymbol) },
  ];
  const maturingTotalCount = (data.maturing.d30 || 0) + (data.maturing.d60 || 0) + (data.maturing.d90 || 0);

  // Window-filtered rows for the two expandable tables (T3-047).
  const visibleMaturing = inWindow(data.maturingList, maturingWindow);
  const visibleEvents = inWindow(data.upcomingPayments, eventsWindow);

  // Unmatched Payments: 3-col layout per bucket (count · status · base-ccy total).
  const unmatchedBreakdown = [
    { count: data.unmatched.overdue_count,  label: 'Overdue',  value: compactMoney(data.unmatched.overdue_total,  currencySymbol) },
    { count: data.unmatched.upcoming_count, label: 'Upcoming', value: compactMoney(data.unmatched.upcoming_total, currencySymbol) },
  ];

  // Pending Approvals: per-type counts from /approvals/summary. Labels match
  // ApprovalsPage.js exactly. 2-col layout — these rows don't carry $ values.
  // Hide zero-count rows so empty states stay tidy.
  const approvalsBreakdown = approvalsSummary
    ? [
        { key: 'new_contracts',  label: 'New Contracts'   },
        { key: 'rate_changes',   label: 'Rate Changes'    },
        { key: 'finalisations',  label: 'Finalisations'   },
        { key: 'amendments',            label: 'Other Amendments'      },
        { key: 'verifications',         label: 'Verifications'         },
        { key: 'queried_verifications', label: 'Queried Verifications' },
      ]
        .map(({ key, label }) => ({ label, value: Number(approvalsSummary[key] || 0) }))
        .filter(r => r.value > 0)
    : [];

  // Top currency breakdown for the Total Portfolio stat card
  const topCurrencyBreakdown = data.capitalByCurrency.slice(0, 3).map(c => ({
    label: c.currency,
    value: compactMoney(c.baseEquivalent, currencySymbol),
  }));

  const overdueCurrencyBreakdown = (data.overdue.by_currency || []).slice(0, 3).map(c => ({
    label: c.currency_code,
    value: `${c.currency_code} ${formatNumber(parseFloat(c.total_amount) || 0)}`,
  }));

  // Contracts Requiring Action: contracts past maturity not yet finalised/rolled
  // (and not mid-amendment), by currency. Count-only — no $ value (it's a count
  // of contracts to action, not an amount due). 2-col layout (count · currency).
  const needsActionBreakdown = (data.needsAction.by_currency || []).slice(0, 3).map(c => ({
    count: parseInt(c.count) || 0,
    label: c.currency_code,
  }));

  // Type breakdown for the Payments (30d) card. NOTE: derived from the
  // upcomingPayments.data list which is capped at 20 rows server-side, so
  // for tenants with >20 upcoming events the buckets won't reconcile to
  // the headline count. TODO: server-side aggregation when we paginate
  // events. See project_dashboard_followups memory.
  // Base-currency conversion uses fxRate from capitalByCurrency (same
  // pivot-cross-rate that the rest of the dashboard already shows).
  const fxByCurrency = (data.capitalByCurrency || []).reduce((acc, c) => {
    if (c.currency && c.fxRate != null) acc[c.currency] = c.fxRate;
    return acc;
  }, {});
  const paymentsByType = (data.upcomingPayments || []).reduce((acc, p) => {
    const t = p.type === 'Interest + Capital' ? 'combined'
      : (p.type === 'Capital' ? 'capital' : 'interest');
    acc[t].count += 1;
    if (p.amount != null) {
      const fx = fxByCurrency[p.currency] ?? 1;
      acc[t].value += p.amount * fx;
    }
    return acc;
  }, {
    interest: { count: 0, value: 0 },
    capital:  { count: 0, value: 0 },
    combined: { count: 0, value: 0 },
  });
  const paymentsTypeBreakdown = [
    { count: paymentsByType.interest.count, label: 'Interest', value: compactMoney(paymentsByType.interest.value, currencySymbol) },
    { count: paymentsByType.capital.count,  label: 'Capital',  value: compactMoney(paymentsByType.capital.value,  currencySymbol) },
    { count: paymentsByType.combined.count, label: 'Combined', value: compactMoney(paymentsByType.combined.value, currencySymbol) },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      {error && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
          <Typography color="warning.main">{error} </Typography>
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Autocomplete
          multiple
          size="small"
          options={availableCountries}
          getOptionLabel={(option) => `${option.country} (${option.country_code})`}
          value={selectedCountries}
          onChange={(_, newValue) => setSelectedCountries(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Country" placeholder="All Countries" />
          )}
          sx={{ minWidth: 300, flex: 1 }}
        />
        <Autocomplete
          size="small"
          options={availableCurrencies.includes(baseCurrency)
            ? availableCurrencies
            : [baseCurrency, ...availableCurrencies]}
          getOptionLabel={(option) => option}
          value={baseCurrency}
          onChange={(_, newValue) => setBaseCurrency(newValue || tenant?.defaultCurrency || 'USD')}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} label="Base Currency" />
          )}
          sx={{ minWidth: 150 }}
        />
      </Paper>

      {/* Row 1 — portfolio shape: Portfolio, Active, Pending, Maturing Soon */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Portfolio Value"
            value={compactMoney(data.totalPortfolioValue, currencySymbol)}
            subtitle={`All currencies in ${baseCurrency}`}
            Icon={TrendingUp}
            color="#00C49F"
            breakdown={topCurrencyBreakdown.length > 0 ? topCurrencyBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Contracts"
            value={data.activeContracts}
            subtitle="Currently in force"
            Icon={Description}
            color="#0088FE"
            onClick={() => navigate('/contracts')}
            breakdown={topProductBreakdown.length > 0 ? topProductBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Approvals"
            value={approvalsTotal}
            subtitle="Require action"
            Icon={Warning}
            color="#FF8042"
            onClick={() => navigate('/approvals')}
            breakdown={approvalsBreakdown.length > 0 ? approvalsBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ position: 'relative', height: '100%' }}>
            <StatCard
              title="Maturing Soon"
              value={maturingTotalCount}
              subtitle={maturingExpanded ? 'Hide contracts' : 'Click to view next 90 days'}
              Icon={EventAvailable}
              color="#FF8042"
              selected={maturingExpanded}
              onClick={() => setMaturingExpanded(prev => !prev)}
              breakdown={maturingBreakdown}
            />
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setMaturingExpanded(prev => !prev); }}
              sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
              aria-label={maturingExpanded ? 'Collapse maturing contracts' : 'Expand maturing contracts'}
            >
              {maturingExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {/* Maturing Soon — collapsed by default, toggled by the Maturing Soon card.
          Lists contracts maturing in the next 90 days; the window toggle filters
          the table to All / 0-30 / 31-60 / 61-90 days. Click a row to open the
          contract (to finalise or roll). */}
      <Collapse in={maturingExpanded} timeout="auto" unmountOnExit>
        <Card sx={{ mb: 3, mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6">Maturing Soon ({windowLabel(maturingWindow)})</Typography>
                <Typography variant="body2" color="text.secondary">
                  {visibleMaturing.length} contract{visibleMaturing.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={maturingWindow}
                  exclusive
                  onChange={makeDisplayChangeHandler(setMaturingWindow)}
                  size="small"
                >
                  <ToggleButton value="all" sx={{ px: 1, py: 0.25 }}>All</ToggleButton>
                  <ToggleButton value="0-30" sx={{ px: 1, py: 0.25 }}>0–30</ToggleButton>
                  <ToggleButton value="31-60" sx={{ px: 1, py: 0.25 }}>31–60</ToggleButton>
                  <ToggleButton value="61-90" sx={{ px: 1, py: 0.25 }}>61–90</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportMaturingContracts(visibleMaturing, windowLabel(maturingWindow))}
                  disabled={visibleMaturing.length === 0}
                >
                  Export
                </Button>
              </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Investor</TableCell>
                    <TableCell>Maturity Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleMaturing.map((row, idx) => (
                    <TableRow
                      key={`maturing-${row.contractId}-${idx}`}
                      hover
                      onClick={() => navigate(`/contracts/${row.contractId}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <ContractReferenceCell reference={row.reference} userReference={row.userReference} />
                      </TableCell>
                      <TableCell>{row.investor}</TableCell>
                      <TableCell>{row.maturityDate ? format(new Date(row.maturityDate), 'dd MMM yyyy') : '—'}</TableCell>
                      <TableCell>
                        <Chip label={row.status} size="small" color={getStatusColor(row.status)} />
                      </TableCell>
                      <TableCell align="right">
                        {row.amount != null
                          ? `${row.currency} ${formatNumber(row.amount)}`
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleMaturing.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">No contracts maturing in this window ({windowLabel(maturingWindow).toLowerCase()})</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Collapse>

      {/* Row 2 — action items: Requiring Action, Overdue, Unmatched, Payments (30d) */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Contracts Requiring Action"
            value={data.needsAction.count}
            subtitle={data.needsAction.count > 0 ? 'Matured — finalise or roll' : 'None to action'}
            Icon={AssignmentLate}
            color="#D32F2F"
            onClick={() => navigate('/contracts?needs_action=true')}
            breakdown={needsActionBreakdown.length > 0 ? needsActionBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Overdue Payments"
            value={data.overdue.count}
            subtitle={data.overdue.count > 0 ? 'Past due, unmatched' : 'None overdue'}
            Icon={ErrorOutline}
            color="#D32F2F"
            onClick={() => navigate('/payments?tab=matching')}
            breakdown={overdueCurrencyBreakdown.length > 0 ? overdueCurrencyBreakdown : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Unmatched Payments"
            value={data.unmatched.count}
            subtitle="Awaiting allocation"
            Icon={LinkOff}
            color="#9C27B0"
            onClick={() => navigate('/payments?tab=matching')}
            breakdown={unmatchedBreakdown}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ position: 'relative', height: '100%' }}>
            <StatCard
              title="Payments (90 days)"
              value={data.upcomingPaymentsCount}
              subtitle={eventsExpanded ? 'Hide events' : 'Click to view events'}
              Icon={PaymentIcon}
              color="#FFBB28"
              selected={eventsExpanded}
              onClick={() => setEventsExpanded(prev => !prev)}
              breakdown={paymentsTypeBreakdown}
            />
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setEventsExpanded(prev => !prev); }}
              sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
              aria-label={eventsExpanded ? 'Collapse upcoming events' : 'Expand upcoming events'}
            >
              {eventsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {/* Upcoming Events — collapsed by default, toggled by the Payments card.
          Spans the next 90 days; the window toggle filters to All / 0-30 / 31-60
          / 61-90 days. Click a row to deeplink into Matching for that contract. */}
      <Collapse in={eventsExpanded} timeout="auto" unmountOnExit>
        <Card sx={{ mb: 3, mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6">Upcoming Events ({windowLabel(eventsWindow)})</Typography>
                <Typography variant="body2" color="text.secondary">
                  {visibleEvents.length} event{visibleEvents.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={eventsWindow}
                  exclusive
                  onChange={makeDisplayChangeHandler(setEventsWindow)}
                  size="small"
                >
                  <ToggleButton value="all" sx={{ px: 1, py: 0.25 }}>All</ToggleButton>
                  <ToggleButton value="0-30" sx={{ px: 1, py: 0.25 }}>0–30</ToggleButton>
                  <ToggleButton value="31-60" sx={{ px: 1, py: 0.25 }}>31–60</ToggleButton>
                  <ToggleButton value="61-90" sx={{ px: 1, py: 0.25 }}>61–90</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportUpcomingEvents(visibleEvents, windowLabel(eventsWindow))}
                  disabled={visibleEvents.length === 0}
                >
                  Export
                </Button>
              </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Investor</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleEvents.map((payment, idx) => {
                    const isRepricing = payment.type === 'Re-pricing';
                    let chipColor = 'secondary';
                    if (isRepricing) chipColor = 'warning';
                    else if (payment.type?.includes('Capital')) chipColor = 'primary';
                    return (
                      <TableRow
                        key={`${payment.type}-${payment.id}-${payment.paymentDate}-${idx}`}
                        hover
                        onClick={() => handleEventClick(payment)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={payment.reference} userReference={payment.userReference} />
                        </TableCell>
                        <TableCell>{payment.investor}</TableCell>
                        <TableCell>{format(new Date(payment.paymentDate), 'dd MMM yyyy')}</TableCell>
                        <TableCell>
                          <Chip label={payment.type} size="small" color={chipColor} />
                        </TableCell>
                        <TableCell align="right">
                          {payment.amount != null
                            ? `${payment.currency} ${formatNumber(payment.amount)}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visibleEvents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">No upcoming events in this window ({windowLabel(eventsWindow).toLowerCase()})</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Collapse>

      <Box sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Row 1: Program Capacity (stacked bars, half) | Status pie | Product pie (quarter each).
            Used + remaining headroom = limit; over-limit shows a red overflow segment.
            Sorted closest-to-breach (backend sort). Top 5 shown by default; expand for the rest. */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <Groups sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  Program Capacity ({data.programCapacity.length})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {data.programCapacity.length > 5 && (
                    <Button
                      size="small"
                      onClick={() => setCapacityExpanded(prev => !prev)}
                      endIcon={capacityExpanded ? <ExpandLess /> : <ExpandMore />}
                    >
                      {capacityExpanded ? 'Show top 5' : `Show all ${data.programCapacity.length}`}
                    </Button>
                  )}
                  <ToggleButtonGroup
                    value={capacityDisplayMode}
                    exclusive
                    onChange={makeDisplayChangeHandler(setCapacityDisplayMode)}
                    size="small"
                  >
                    <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }}>%</ToggleButton>
                    <ToggleButton value="number" sx={{ px: 1, py: 0.25 }}>#</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              {data.programCapacity.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(300, (capacityExpanded ? data.programCapacity.length : Math.min(5, data.programCapacity.length)) * 56)}>
                  <BarChart
                    key={capacityDisplayMode}
                    data={capacityExpanded ? data.programCapacity : data.programCapacity.slice(0, 5)}
                    layout="vertical"
                    margin={{ left: 16, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    {capacityDisplayMode === 'percent' ? (
                      <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    ) : (
                      <XAxis type="number" tickFormatter={(value) => `${currencySymbol} ${(value / 1_000_000).toFixed(1)}M`} />
                    )}
                    <YAxis type="category" dataKey="programName" width={200} tick={{ fontSize: 12 }} interval={0} />
                    <Tooltip
                      formatter={(value, name) => [
                        capacityDisplayMode === 'percent'
                          ? `${Number(value).toFixed(1)}%`
                          : compactMoney(value, currencySymbol),
                        name,
                      ]}
                      labelFormatter={(label, payload) => {
                        const p = payload && payload[0]?.payload;
                        if (!p) return label;
                        const util = p.utilisation != null ? `${(p.utilisation * 100).toFixed(1)}%` : 'No limit';
                        return `${p.programName} · ${p.issuerName} · ${util}`;
                      }}
                    />
                    <Legend />
                    <Bar dataKey={capacityDisplayMode === 'percent' ? 'usedPct' : 'usedSegment'}         stackId="cap" fill={capacityColors.used}     name="Used" />
                    <Bar dataKey={capacityDisplayMode === 'percent' ? 'headroomPct' : 'headroomSegment'} stackId="cap" fill={capacityColors.headroom} name="Headroom" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No active programs</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contracts by Status (pie) */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Contracts by Status
                </Typography>
                <ToggleButtonGroup
                  value={statusDisplayMode}
                  exclusive
                  onChange={makeDisplayChangeHandler(setStatusDisplayMode)}
                  size="small"
                >
                  <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }}>%</ToggleButton>
                  <ToggleButton value="number" sx={{ px: 1, py: 0.25 }}>#</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {data.contractsByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      key={statusDisplayMode}
                      data={data.contractsByStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderPieLabel(statusDisplayMode)}
                      outerRadius={70}
                      fill={primary}
                      dataKey="value"
                    >
                      {data.contractsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartPalette[index % chartPalette.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No contract data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contracts by Product (pie) — paired beside Status */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Contracts by Product Type
                </Typography>
                <ToggleButtonGroup
                  value={productDisplayMode}
                  exclusive
                  onChange={makeDisplayChangeHandler(setProductDisplayMode)}
                  size="small"
                >
                  <ToggleButton value="percent" sx={{ px: 1, py: 0.25 }}>%</ToggleButton>
                  <ToggleButton value="number" sx={{ px: 1, py: 0.25 }}>#</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {data.contractsByProduct.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      key={productDisplayMode}
                      data={data.contractsByProduct}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderPieLabel(productDisplayMode)}
                      outerRadius={70}
                      fill={primary}
                      dataKey="value"
                    >
                      {data.contractsByProduct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartPalette[index % chartPalette.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No product data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 2: Top Investors (bar) | Capital by Currency (table) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Top Investors by Capital Deployed
              </Typography>
              {data.contractsByInvestor.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  {/* Single stacked bar per investor: Approved/Active at the base,
                      Draft above (bar height = full deployed capital). The two
                      segments share one stackId; the fill now encodes status, so
                      the old per-Cell "Other"-recolouring no longer applies. */}
                  <BarChart data={data.contractsByInvestor}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `${currencySymbol} ${(value / 1000000).toFixed(0)}M`} />
                    <Tooltip formatter={(value, name) => [`${currencySymbol} ${(value / 1000000).toFixed(2)}M`, name]} />
                    <Legend />
                    <Bar dataKey="approved" stackId="cap" fill={investorColors.bar} name={`Approved (${baseCurrency})`} />
                    <Bar dataKey="draft" stackId="cap" fill={investorColors.other} name={`Draft (${baseCurrency})`} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No investor data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Capital by Currency — moved up, paired beside Top Investors */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Capital by Currency
              </Typography>
              {data.capitalByCurrency.length > 0 ? (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  {/* Grouped columns: shared Currency / Rate, then a Draft block
                      (Contracts, LCY, base-equiv) and an Approved/Active block
                      (Contracts, LCY, base-equiv) — each bucket owns its own
                      contract count. Rate is shown once — the FX rate is identical
                      across statuses. A single Total row carries each group's
                      base-currency total under its own group columns (Draft total
                      + Approved/Active total; no combined grand total). 8-column
                      grid; uniform default borders. */}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell rowSpan={2}>Currency</TableCell>
                        <TableCell rowSpan={2} align="right">Rate</TableCell>
                        <TableCell colSpan={3} align="center">Draft</TableCell>
                        <TableCell colSpan={3} align="center">Approved</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="right">Contracts</TableCell>
                        <TableCell align="right">LCY Amount</TableCell>
                        <TableCell align="right">{baseCurrency} Equiv</TableCell>
                        <TableCell align="right">Contracts</TableCell>
                        <TableCell align="right">LCY Amount</TableCell>
                        <TableCell align="right">{baseCurrency} Equiv</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.capitalByCurrency.map((curr) => (
                        <TableRow key={curr.currency}>
                          <TableCell><strong>{curr.currency}</strong></TableCell>
                          <TableCell align="right">
                            {curr.fxRate != null ? curr.fxRate.toFixed(4) : '-'}
                          </TableCell>
                          <TableCell align="right">{curr.draftCount || '-'}</TableCell>
                          <TableCell align="right">
                            {curr.draftAmount ? formatNumber(curr.draftAmount) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {curr.draftBaseEquivalent ? `${currencySymbol} ${formatNumber(curr.draftBaseEquivalent)}` : '-'}
                          </TableCell>
                          <TableCell align="right">{curr.approvedCount || '-'}</TableCell>
                          <TableCell align="right">
                            {curr.approvedAmount ? formatNumber(curr.approvedAmount) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {curr.approvedBaseEquivalent ? `${currencySymbol} ${formatNumber(curr.approvedBaseEquivalent)}` : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell colSpan={3}><strong>Total</strong></TableCell>
                        <TableCell colSpan={2} align="right">
                          <strong>{currencySymbol} {formatNumber(data.totalPortfolioDraft)}</strong>
                        </TableCell>
                        <TableCell />
                        <TableCell colSpan={2} align="right">
                          <strong>{currencySymbol} {formatNumber(data.totalPortfolioApproved)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No currency data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
